import {
  CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT,
  useDealerInfluencedEntityAdminSpaceParameters,
} from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useModeFilter, usePricingTierFilters, useRoleAuth } from '@src/hooks';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import { ACTION, DEFAULT_MAX_ROWS, DEFAULT_PAGINATION_SIZE, EntityAdmin, type SymbolGroup } from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { SymbolGroupChildrenDrawer } from './SymbolGroupChildrenDrawer';
import { getSymbolGroupsPatchDeletePath, SYMBOL_GROUPS_COLUMNS, SYMBOL_GROUPS_DRAWER_OPTIONS } from './utils';

export const CustomerSymbolGroups = () => {
  const { isAuthorized } = useRoleAuth();
  const { getConfig } = useOrgConfiguration();

  const modeFilter = useModeFilter();

  const { symbolGroupFilter } = usePricingTierFilters({
    // field "SymbolGroup" --> Represented as "Tier" in Symbol Groups
    symbolGroupField: 'Tier',
  });
  const availableFilterableProperties = useMemo(() => {
    const properties = [modeFilter, symbolGroupFilter];
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [modeFilter, symbolGroupFilter]);
  const spaceParameterProps = useDealerInfluencedEntityAdminSpaceParameters(
    CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  return (
    <EntityAdmin<SymbolGroup>
      title="Symbol Groups"
      subtitle="Set up your Symbol Groups here."
      path="/symbol-groups"
      getPatchPath={getSymbolGroupsPatchDeletePath}
      columns={SYMBOL_GROUPS_COLUMNS}
      entityIDField="Tier"
      entityName="Symbol Group"
      allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowDeleteEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowModeSwitch={{ isDisabled: () => !isAuthorized(ACTION.EDIT_DEALER) }}
      {...spaceParameterProps}
      persistKey="symbol-groups-with-custom-drawer"
      blotterRowCount={getConfig(OrgConfigurationKey.CustomerSymbolGroupsRowsMax, DEFAULT_MAX_ROWS)}
      paginationSize={getConfig(OrgConfigurationKey.CustomerSymbolGroupsPageSize, DEFAULT_PAGINATION_SIZE)}
      customDrawerLayout={SymbolGroupChildrenDrawer}
      drawerType="custom"
      drawerOptions={SYMBOL_GROUPS_DRAWER_OPTIONS}
      suppressEditingFooterActions={true}
    />
  );
};
